import { Record } from 'immutable';
import _ from 'lodash';
import uuid from 'uuid';

import config from '../config';
import PlusBus from './PlusBus';
import Search from './Search';
import SeasonSearch from './SeasonSearch';
import OverallCheapestSelection from './OverallCheapestSelection';
import DaySaveSearch from './DaySaveSearch';
import RoversRangersSearch from './RoversRangersSearch';
import SmartCarnetRecordSearch from "./smartCarnet/SmartCarnetRecordSearch";
import TripFares from './TripFares';
import Photocard from './Photocard';
import DisplayFilters from './DisplayFilters';
import SeatingPreferences from './SeatingPreferences';
import Service from './Service';
import { resolve } from '../helpers/LinkMapper';
import {
    TripTypes,
    TicketTypes,
    JourneyDirections,
    TimeTypes,
    FareCategories,
    FareClasses,
    JourneyPlannerLayout,
} from '../constants/AppConstants';
import Passengers from './Passengers';

const defaults = {
    id: null,
    fares: new TripFares(),
    fareCategory: null,
    fareCategoryDetail: null,
    fareTicketTypeCode: null,
    fareRouteCode: null,
    fareDestination: null,
    fareOrigin: null,
    selectingCheapestFare: true,
    fareClass: null,
    outwardJourney: null,
    returnJourney: null,
    reservationPreferences: new SeatingPreferences(),
    photocard: new Photocard(),
    passengers: new Passengers(),
    orderTripId: null,
    search: new Search(),
    plusBusAvailability: null,
    cheapest: new OverallCheapestSelection(),
    cheapestFastest: new OverallCheapestSelection(),
    cheapestFirstClass: new OverallCheapestSelection(),
    filters: new DisplayFilters(),
    hasServiceProvider: false,
    listStep: null,
    raw: true
};


//HACK: make this better
function _searchClass (search, searchClassName) {
    if (searchClassName) {
        switch (searchClassName) {
            case 'DaySaveSearch': return DaySaveSearch;
            case 'SeasonSearch': return SeasonSearch;
            case 'RoversRangersSearch': return RoversRangersSearch;
            case 'SmartCarnetRecordSearch': return SmartCarnetRecordSearch;
            default: return Search;
        }
    }
    if (search.date && search.origin) {
        return DaySaveSearch;
    }
    if (search.date && search.ticketType) {
        return RoversRangersSearch;
    }
    if (search.dates) {
        return SeasonSearch;
    }
    return Search;
}

class Trip extends Record(defaults) { // eslint-disable-line new-cap

    constructor (values = {}) {
        if (!values.id) {
            values.id = uuid.v1();
        }

        super(values);
    }


    static fromJSON (json = {}) {
        const searchClass = _searchClass(json.search, json.searchClass);

        return new Trip({
            id: json.id,
            fares: resolve(json.fares).as(TripFares),
            fareCategory: json.fareCategory,
            fareClass: json.fareClass,
            outwardJourney: resolve(json.outwardJourney).as(Service),
            returnJourney: resolve(json.returnJourney).as(Service),
            reservationPreferences: resolve(json.reservationPreferences).as(SeatingPreferences),
            photocard: resolve(json.photocard).as(Photocard),
            passengers: resolve(json.passengers).as(Passengers),
            orderTripId: json.orderTripId,
            search: resolve(json.search).as(searchClass),
            plusBusAvailability: resolve(json.plusBusAvailability).as(PlusBus),
            cheapest: resolve(json.cheapest).as(OverallCheapestSelection),
            cheapestFastest: resolve(json.cheapestFastest).as(OverallCheapestSelection),
            cheapestFirstClass: resolve(json.cheapestFirstClass).as(OverallCheapestSelection),
            filters: resolve(json.filters).as(DisplayFilters),
            hasServiceProvider: json.hasServiceProvider,
            listStep: json.listStep,
            raw: json.raw
        });
    }

    getType () {

        if (this.search instanceof DaySaveSearch) {
            return TripTypes.DAYSAVE;
        }

        if (this.search instanceof RoversRangersSearch) {
            return TripTypes.ROVERSRANGERS;
        }

        const isSeason = this.search instanceof SeasonSearch;

        if (this.isSmartcard()) {
            if (isSeason) {
                return TripTypes.SMARTCARD_SEASON;
            }
            return TripTypes.SMARTCARD_POINT;
        }

        if (isSeason) {
            return TripTypes.SEASON;
        }
        return TripTypes.POINT;
    }

    needsJourney () {
        return this.getType() === TripTypes.POINT || this.getType() === TripTypes.SMARTCARD_POINT;
    }

    isPoint () {
        return this.search instanceof Search;
    }

    isSeason () {
        return this.search instanceof SeasonSearch;
    }

    isDaySave () {
        return this.search instanceof DaySaveSearch;
    }

    isRovers () {
        return this.search instanceof RoversRangersSearch;
    }

    isSingle () {
        return !this.isSeason() && this.getTicketType() === TicketTypes.SINGLE;
    }

    isReturn () {
        return !this.isSeason() && this.getTicketType() === TicketTypes.RETURN;
    }

    isOpenReturn () {
        return !this.isSeason() && this.getTicketType() === TicketTypes.OPEN_RETURN;
    }

    getTicketType () {
        return this.search.ticketType;
    }

    isSmartcard () {
        return !!this.search.smartcard;
    }

    getFareForDirection (direction) {
        if (direction === JourneyDirections.INWARD) {
            return this.fares.returnSingle;
        }
        return this.fares.return || this.fares.outwardSingle;
    }

    getServiceForDirection (direction) {
        return direction === JourneyDirections.INWARD ? this.returnJourney : this.outwardJourney;
    }

    setFareForDirection (direction, fare) {
        const blankFares = new TripFares({
            plusBus: this.fares.plusBus
        });
        if (fare && fare.isReturn()) {
            return this.set('fares', blankFares.set('return', fare));
        }

        if (direction === JourneyDirections.INWARD) {
            return this.set(
                'fares',
                blankFares
                    .set('returnSingle', fare)
                    .set('outwardSingle', this.fares.get('outwardSingle'))
            );
        }

        return this.set(
            'fares',
            blankFares
                .set('outwardSingle', fare)
                .set('returnSingle', this.fares.get('returnSingle'))
        );
    }

    setFareOnlyFare (fare) {
        return this.set('fares', new TripFares({
            fareOnly: fare
        }));
    }

    resetFares () {
        return this.set('fares', new TripFares({
            plusBus: this.fares.plusBus
        }));
    }

    getOysterPrice () {
        if (this.isSingle()) {
            if (this.outwardJourney) {
                return this.outwardJourney.oysterPrice || null;
            }
        }
        if (this.isReturn()) {
            if (this.outwardJourney && this.returnJourney) {
                if (this.outwardJourney.oysterPrice && this.returnJourney.oysterPrice) {
                    return this.outwardJourney.oysterPrice + this.returnJourney.oysterPrice;
                }
            }
        }
        return null;
    }

    resetSelected (direction) {
        if (direction === JourneyDirections.INWARD && this.search.targetLayout === JourneyPlannerLayout.LIST) {
            return this.setIn(['fares', 'returnSingle'], null)
                .set('returnJourney', null);
        }

        return this.set('fares', new TripFares())
            .set('fareClass', null)
            .set('returnJourney', null)
            .set('outwardJourney', null)
            .set('photocard', new Photocard());
    }

    setServiceForDirection (direction, service) {
        const journeyKey = direction === JourneyDirections.INWARD ? 'returnJourney' : 'outwardJourney';
        return this.set(journeyKey, service);
    }

    setSeatingPreferences (seatingPreferences) {
        return this.set('reservationPreferences', seatingPreferences);
    }

    setSeatingForDirection (direction, fare, service) {
        const shouldReserve = fare.isSeatCompulsory() || service.isSeatCompulsory() || service.isSeatAvailable();
        let newTrip = this.setIn(['reservationPreferences', direction], shouldReserve);

        if (!newTrip.reservationPreferences.shouldReserve()) {
            newTrip = newTrip.set('reservationPreferences', newTrip.reservationPreferences.resetPreferences());
        }
        return newTrip;
    }

    hasSeatingPreferences () {
        return this.reservationPreferences.hasPreferences();
    }

    getOutwardFare () {
        return this.getFareForDirection(JourneyDirections.OUTWARD);
    }

    hasOutwardFare () {
        return !!this.getOutwardFare();
    }

    getOutwardService () {
        return this.outwardJourney;
    }

    hasOutwardService () {
        return !!this.getOutwardService();
    }

    getInwardFare () {
        return this.getFareForDirection(JourneyDirections.INWARD);
    }

    hasInwardFare () {
        return !!this.getInwardFare();
    }

    getFareOnlyFare () {
        return this.fares.fareOnly;
    }

    hasFareOnlyFare () {
        return !!this.fares.fareOnly;
    }

    getReturnFare () {
        return this.fares.return;
    }

    hasReturnFare () {
        return !!this.fares.return;
    }

    getInwardService () {
        return this.returnJourney;
    }

    hasInwardService () {
        return !!this.getInwardService();
    }

    getPlusBus () {
        return this.fares.plusBus;
    }

    hasPlusBus () {
        return Boolean(this.getPlusBus().count());
    }

    setPlusBus (plusBus) {
        return this.setIn(['fares', 'plusBus'], plusBus);
    }

    getServiceOrderForDirection (direction) {
        const { arriveDepart } = this.search.getTimebandForDirection(direction);
        return arriveDepart === TimeTypes.DEPART ? 'origin' : 'destination';
    }

    isSeatingAvailable () {
        const services = _.compact([
            this.getOutwardService(),
            this.getInwardService()
        ]);

        return _.some(_.invokeMap(services, 'isSeatAvailable'));
    }

    hasSeasonFare () {
        return !!this.getSeasonFare();
    }

    getSeasonFare () {
        return this.fares.season;
    }

    setSeasonFare (fare) {
        return this.setIn(['fares', 'season'], fare);
    }

    hasDaySaveFare () {
        return !!this.getDaySaveFare();
    }

    getDaySaveFare () {
        return this.fares.daySave;
    }

    setDaySaveFare (fare) {
        return this.setIn(['fares', 'daySave'], fare);
    }

    hasRoversRangersFare () {
        return !!this.getRoversRangersFare();
    }

    getRoversRangersFare () {
        return (this.getOutwardFare() && this.getOutwardFare().isRoversRangers()) ? this.getOutwardFare() : null;
    }

    setRoversRangersFare (fare) {
        return this.setIn(['fares', 'roversRangers'], fare);
    }

    getTotalPrice () {
        let total = this.getPlusBus().getTotalPrice();
        if (this.hasOutwardFare()) {
            total += this.getOutwardFare().getPrice();
        }
        if (this.hasInwardFare()) {
            total += this.getInwardFare().getPrice();
        }
        if (this.hasSeasonFare()) {
            total += this.getSeasonFare().getPrice();
        }
        if (this.hasFareOnlyFare()) {
            total += this.getFareOnlyFare().getPrice();
        }

        return total;
    }

    getTotalPriceWithoutPromo() {
        let total = this.getPlusBus().getTotalPrice();

        if (this.hasOutwardFare()) {
            total += this.getOutwardFare().getOriginalTotalPrice();
        }
        if (this.hasInwardFare()) {
            total += this.getInwardFare().getOriginalTotalPrice();
        }
        if (this.hasSeasonFare()) {
            total += this.getSeasonFare().getOriginalTotalPrice();
        }
        if (this.hasFareOnlyFare()) {
            total += this.getFareOnlyFare().getOriginalTotalPrice();
        }

        return total;
    }

    getTotalPriceWithoutDiscount () {
        let total = this.getPlusBus().getTotalPrice();

        const getRailcardDiscount = (fare) => fare.tickets.reduce((tot, ticket) => tot + (ticket.railcardDiscount || 0), 0);

        if (this.hasOutwardFare()) {
            total += this.getOutwardFare().getPrice() + getRailcardDiscount(this.getOutwardFare());
        }
        if (this.hasInwardFare()) {
            total += this.getInwardFare().getPrice() + getRailcardDiscount(this.getInwardFare());
        }
        if (this.hasSeasonFare()) {
            total += this.getSeasonFare().getPrice() + getRailcardDiscount(this.getSeasonFare());
        }
        if (this.hasFareOnlyFare()) {
            total += this.getFareOnlyFare().getPrice() + getRailcardDiscount(this.getFareOnlyFare());
        }

        return total;
    }

    isJPValid () {
        if (this.hasFareOnlyFare()) {
            return true;
        }

        const outward = this.getOutwardFare();

        if (!this.hasOutwardService() || !this.hasOutwardFare()) {
            return false;
        }

        if (this.isReturn() && !outward.isReturn() && !this.hasInwardFare()) {
            return false;
        }

        if (this.isReturn() && outward.isReturn() && !this.hasInwardService()) {
            return false;
        }

        return !this.servicesOverlap();
    }

    /**
     * When you attempt to evaluate a seasons trip make sure if it isSmartcard() you check that
     * smartcard is set and that smartcard.canLoadProduct()
     *
     * @returns Boolean
     * */
    isSeasonsValid () {
        if (this.hasFareOnlyFare() && this.getFareOnlyFare().isSeason()) {
            return (this.isSmartcard() || this.photocard.isComplete());
        }

        return this.search.isValid() && this.hasSeasonFare();
    }

    isDaySaveValid () {
        return this.search.isValid() && this.hasOutwardFare();
    }

    isRoversValid () {
        return this.search.isComplete() && this.hasOutwardFare() && this.passengers.isComplete();
    }

    isValid () {
        if (this.isSeason()) {
            return this.isSeasonsValid();
        } else if (this.isDaySave()) {
            return this.isDaySaveValid();
        } else if (this.isRovers()) {
            return this.isRoversValid();
        }
        return this.isJPValid();
    }

    servicesOverlap () {
        return this.isReturn()
            && this.hasInwardService()
            && this.hasOutwardService()
            && !this.getInwardService().getDepartureDateTime().isAfter(this.getOutwardService().getArrivalDateTime());
    }

    isCheapestFare (fare) {
        return this.cheapest.isCheapestFare(fare);
    }

    getCheapestFareCategory (cheapest = this.cheapest) {
        if ((this.isReturn() || this.search.isReturnLondonTravelcard()) && cheapest.hasReturnFare()) {
            return cheapest.getReturnFare().category;
        } else if (this.isReturn() && (cheapest.hasOutwardFare() || cheapest.hasInwardFare())) {
            return FareCategories.ADVANCE;
        } else if (cheapest.hasOutwardFare()) {
            return cheapest.getOutwardFare().category;
        }

        return FareCategories.ADVANCE;
    }

    getCheapestFareClass (cheapest = this.cheapest) {
        if (this.isReturn() && cheapest.hasReturnFare()) {
            return cheapest.getReturnFare().getClass();
        } else if (this.isReturn() && (cheapest.hasOutwardFare() || cheapest.hasInwardFare())) {
            return FareClasses.STANDARD;
        } else if (cheapest.hasOutwardFare()) {
            return cheapest.getOutwardFare().getClass();
        }

        return FareClasses.STANDARD;
    }

    getCheapestFareFromOverAll (fareCategory, fareClass, journeyDirection) {
        if (
            this.getCheapestFareCategory() !== fareCategory
            || this.getCheapestFareClass() !== fareClass
            || (fareCategory === FareCategories.ADVANCE && this.cheapest.hasReturnFare())
            || (fareCategory !== FareCategories.ADVANCE && this.isReturn() && !this.cheapest.hasReturnFare())
        ) {
            return null;
        }

        return this.cheapest.getFareForDirection(journeyDirection);
    }

    isPlusBusAvailable () {
        if (this.isSmartcard()){
            return config.get('enablePlusBus') && config.get('enableSmartPlusBus');
        }
        return config.get('enablePlusBus');
    }

    getOutwardJourneyTrainRid () {
        return this.outwardJourney.journey.legs
            && this.outwardJourney.journey.legs.first()
            && this.outwardJourney.journey.legs.first().serviceDetails.trainRid;
    }
}

export default Trip;



// WEBPACK FOOTER //
// ./src/records/Trip.js